import {
  ArrowDownLeftIcon,
  ArrowUpRightIcon,
  DevicePhoneMobileIcon,
  EnvelopeIcon,
  InboxIcon,
  PencilSquareIcon,
  PhoneIcon,
  UserIcon,
} from '@heroicons/react/24/outline'
import classNames from 'classnames'
import { ReactElement, useState } from 'react'
import type { CommLogEntryFields } from 'app/types/comm-log'
import { ActivityCard } from 'app/components/activities/ActivityCard'
import { KeyValueDrawer } from 'app/components/activities/KeyValueDrawer'
import {
  COMM_MODE_EMAIL,
  COMM_MODE_IN_PERSON,
  COMM_MODE_MAIL,
  COMM_MODE_NOTE,
  COMM_MODE_PHONE,
  COMM_MODE_TEXT,
  COMM_SOURCE_USER,
  commLogModeLabel,
  isHumanCommLogEntry,
  SENT_OR_RECEIVED_RECEIVED,
  SENT_OR_RECEIVED_SENT,
} from 'app/lib/commLog'

const MODE_ICONS = {
  [COMM_MODE_NOTE]: PencilSquareIcon,
  [COMM_MODE_EMAIL]: EnvelopeIcon,
  [COMM_MODE_MAIL]: InboxIcon,
  [COMM_MODE_PHONE]: PhoneIcon,
  [COMM_MODE_IN_PERSON]: UserIcon,
  [COMM_MODE_TEXT]: DevicePhoneMobileIcon,
}

/**
 * Slate rather than the greens and blacks the Trackable-native cards use.
 * These records were written in the practice management system, not here, and
 * the feed should not imply otherwise. Automated traffic sits a shade lighter
 * than something a person typed.
 */
const iconClasses = (isHuman: boolean) =>
  classNames(`h-5 w-5 shrink-0 rounded-md p-1 text-on-color`, isHuman ? `bg-slate-600` : `bg-slate-400`)

/** Long notes clamp; the PMS writes some that run to several paragraphs. */
const NOTE_EXPAND_THRESHOLD = 220

export function CommLogEntry({
  entry,
  isFirst = true,
  isLast = true,
  hideDate,
}: {
  entry: CommLogEntryFields
  isFirst?: boolean
  isLast?: boolean
  hideDate?: boolean
}): ReactElement {
  const [expanded, setExpanded] = useState(false)

  const isHuman = isHumanCommLogEntry(entry)
  const isReceived = entry.sentOrReceived === SENT_OR_RECEIVED_RECEIVED
  const isSent = entry.sentOrReceived === SENT_OR_RECEIVED_SENT
  const Icon = (entry.mode != null && MODE_ICONS[entry.mode]) || PencilSquareIcon
  const DirectionIcon = isReceived ? ArrowDownLeftIcon : ArrowUpRightIcon
  const note = entry.note?.trim()

  /*
   * The source leads the title. A Trackable "Inbound Call" and a PMS "Phone"
   * record can describe the same real call, and on a merged feed the reader has
   * no other way to tell which system they are looking at. Muted, because it is
   * provenance rather than the substance of the row.
   */
  const title = (
    <div className="flex min-w-0 flex-wrap items-center gap-x-1.5 text-ui-sm text-strong">
      <span className="shrink-0 text-body-xs uppercase tracking-wide text-minimal">PMS</span>
      <span>{commLogModeLabel(entry.mode)}</span>
      {(isReceived || isSent) && (
        <DirectionIcon
          className={classNames(`h-3.5 w-3.5 shrink-0`, isReceived ? `text-info-default` : `text-minimal`)}
        />
      )}
      {entry.commTypeName && <span className="truncate text-body-xs text-subtle">{entry.commTypeName}</span>}
      {entry.commSource === COMM_SOURCE_USER && (
        <span className="truncate text-body-xs text-subtle">{entry.userName ?? `Staff`}</span>
      )}
    </div>
  )

  return (
    <ActivityCard
      icon={<Icon className={iconClasses(isHuman)} />}
      title={title}
      activityDate={hideDate ? undefined : entry.commDate}
      isFirst={isFirst}
      isLast={isLast}
      preview={
        <div className="w-full rounded-lg border border-default bg-surface px-4 py-3 text-body-xs shadow-sm">
          {note ? (
            <>
              {/*
                Rendered verbatim. The PMS writes its own prefixes ("Patient Text
                Sent via PbN", "Phone Call Received at ... Call Summary:") and we
                deliberately do not parse them: this surface is meant to show the
                truth of the record.
              */}
              <p
                className={classNames(
                  `whitespace-pre-wrap break-words text-body-sm`,
                  isHuman ? `text-strong` : `text-subtle`,
                  !expanded && `line-clamp-4`,
                )}
              >
                {note}
              </p>
              {note.length > NOTE_EXPAND_THRESHOLD && (
                <button
                  type="button"
                  className="mt-1 text-body-xs text-info-default hover:underline"
                  onClick={() => setExpanded((current) => !current)}
                >
                  {expanded ? `Show less` : `Show more`}
                </button>
              )}
            </>
          ) : (
            <p className="text-body-sm italic text-minimal">No note text</p>
          )}
        </div>
      }
      drawer={
        /*
         * The untouched record. TRK-5560 exists so we can decide what of the PMS
         * object belongs in the product, which means every field has to stay
         * reachable while that decision is open.
         */
        <div className="w-full rounded-lg border border-default bg-surface px-4 py-3 text-body-xs shadow-sm">
          <KeyValueDrawer
            map={{
              'Comm type': entry.commTypeName ?? (entry.commType ? String(entry.commType) : undefined),
              Staff: entry.userName ?? (entry.userNum ? `Staff` : undefined),
              Ended: entry.commEndDate,
              'Vendor ID': entry.vendorId,
              'PMS patient': entry.pmsPatientId,
              Mode: `${commLogModeLabel(entry.mode)}${entry.mode == null ? `` : ` (${entry.mode})`}`,
              Source: entry.commSource == null ? undefined : String(entry.commSource),
              Program: entry.programNum == null ? undefined : String(entry.programNum),
              'PMS updated': entry.pmsUpdatedDate,
            }}
          />
        </div>
      }
    />
  )
}
